import React, { useState } from 'react';
import * as Yup from 'yup';
import { Button } from '@economic/taco';

import ExternalLink from '../../shared/components/ExternalLink';
import { Field, Form } from '../../shared/components/Form';
import { t, tBB } from '../../shared/components/l10n/l10n';
import { ProgressDots } from '../../shared/components/ui/progressDots/ProgressDots';
import { OnboardingStatusSetups } from './types';
import internalApiClient from '../../shared/util/api/internalApiClient';

const validationSchema = Yup.object().shape({
    // trick to force selecting some setup (0 is invalid), required won't work
    setupId: Yup.number().min(1, () => t`ONBOARDING_GETTINGSTARTED_COMPANYTYPE_MUSTCHOOSESETUP`),
});

const onboardingCopySetup = setupId => internalApiClient.put(`/onboarding/standard-setup`, { setupId, copyLayout: true });

interface Props {
    setups: OnboardingStatusSetups;
    onBack: () => void;
    onDone: () => void;
    onSkip: () => void;
    stepCount: number;
}

export default function StepCompanyType({ setups, onBack, onDone, onSkip, stepCount }: Props) {
    const [seeMore, setSeeMore] = useState(false);

    async function copySetup({ setupId }: any) {
        await onboardingCopySetup(setupId);
        onDone();
    }

    const chosenDefaultSetup = setups.setups.find(s => s.setup === setups.chosenDefaultSetup);
    const { canApplyDefaultSetup, errorsPreventingApplyingStandardSetup } = setups;

    // split in two groups
    const favorites = setups.setups.filter(s => s.isFavorite);
    const other = setups.setups.filter(s => !s.isFavorite);

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__company-type text-center">
                <ProgressDots stepsCount={stepCount} currentStep={2} />

                <h1>{t`ONBOARDING_GETTINGSTARTED_COMPANYTYPE_TITLE`}</h1>
                <p>
                    {tBB('ONBOARDING_GETTINGSTARTED_COMPANYTYPE_CONTENT', {
                        __LINK: content => (
                            <ExternalLink href="http://wiki2.e-conomic.dk/indstillinger/regnskab-standardopsaetning">
                                {content}
                            </ExternalLink>
                        ),
                    })}
                </p>

                {!!chosenDefaultSetup ? (
                    <>
                        <p>
                            {t`GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED`}
                            <br />
                            <strong>{chosenDefaultSetup.name}</strong>
                        </p>

                        <p>
                            {t`GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_I`}
                            <br />
                            {t`GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_I`}
                            <i className="long-arrow-right" />
                            {t`GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_II`}
                            <i className="long-arrow-right" />
                            {t`GETTING_STARTED_DEFAULT_SETUP_NAME_ALREADY_APPLIED_HINT_CHANGE_II_PART_III`}
                        </p>

                        <div className="onboarding__navigation-buttons">
                            <Button appearance="primary" onClick={onSkip}>{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                            <Button appearance="discrete" onClick={onBack}>{t`ONBOARDING_GETTINGSTARTED_BACK`}</Button>
                        </div>
                    </>
                ) : !canApplyDefaultSetup ? (
                    <>
                        <p>
                            <strong>{t`GETTING_STARTED_CANNOT_APPLY_DEFAULT_SETUP_REASON`}</strong>
                        </p>
                        <ul>
                            {errorsPreventingApplyingStandardSetup.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                        <p>
                            <strong>{t`GETTING_STARTED_DEFAULT_SETUP_CANNOT_APPLY_MORE_INFO`}</strong>
                        </p>
                        <div className="onboarding__navigation-buttons">
                            <Button appearance="primary" onClick={onSkip}>{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                            <Button appearance="discrete" onClick={onBack}>{t`ONBOARDING_GETTINGSTARTED_BACK`}</Button>
                        </div>
                    </>
                ) : (
                    <div>
                        <Form
                            skipNotifications
                            onSubmit={copySetup}
                            validationSchema={validationSchema}
                            validateOnBlur={false}
                            initialData={{ setupId: Number(setups.chosenDefaultSetup) }}>
                            <div className="onboarding__form-content">
                                <div className="onboarding__chart-of-account-list">
                                    <Field.NumberRadioGroup
                                        name="setupId"
                                        options={favorites.map(setup => ({ text: setup.name, value: setup.setup }))}
                                    />

                                    {other.length > 0 && (
                                        <>
                                            {!seeMore && (
                                                <Button
                                                    className="see-more"
                                                    appearance="discrete"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        setSeeMore(!seeMore);
                                                    }}>{t`ONBOARDING_GETTINGSTARTED_COMPANYTYPE_SEEMORE`}</Button>
                                            )}
                                            {seeMore && (
                                                <Field.NumberRadioGroup
                                                    name="setupId"
                                                    options={other.map(setup => ({ text: setup.name, value: setup.setup }))}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="onboarding__navigation-buttons">
                                <Button appearance="primary" type="submit">{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                                <Button
                                    appearance="discrete"
                                    onClick={e => {
                                        e.preventDefault();
                                        onBack();
                                    }}>{t`ONBOARDING_GETTINGSTARTED_BACK`}</Button>
                            </div>
                        </Form>
                    </div>
                )}
            </div>
        </div>
    );
}
